import { useRef } from "react";
import { ImagePlus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useImageAsset } from "@/hooks/useImageAsset";
import {
  LOGO_CORNERS,
  LOGO_SIZE_DEFAULT,
  LOGO_SIZE_MAX,
  LOGO_SIZE_MIN,
} from "@/lib/deck-logo";
import { deleteAsset, putAsset } from "@/lib/idb";
import { deckOwner } from "@/lib/ownerKey";
import { cn } from "@/lib/utils";
import { setLogo } from "@/store/documentSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import type { LogoCorner } from "@/types/document";

// §24.3 The Logo right-panel: pick one image, pick a corner, pick a size. Built like
// `TransitionPanel` (§10.3) because it is the same shape of thing — deck-level state, one
// reducer, and the persistence listener does the rest (§24.3.1).
//
// There is no preview pane here, deliberately (§24.3.2): the live preview is the deck
// itself, which is on screen to the left of this panel showing every slide with the mark
// already on it. A 16:9 demo card would be a worse copy of something the user is looking at.
export function LogoPanel() {
  const dispatch = useAppDispatch();
  const deckId = useAppSelector((s) => s.document.id);
  const logo = useAppSelector((s) => s.document.logo);
  const previewUrl = useImageAsset(logo?.assetId);
  const fileRef = useRef<HTMLInputElement>(null);

  // §24.0.4 Pick/Replace: write the new blob FIRST, point the deck at it, then drop the old
  // one. Deleting first would leave a window where the record points at a blob that is
  // already gone; deleting last (and only after the record moved) means the orphan rule
  // holds either way round — nothing in `assets` is ever unreferenced, and nothing
  // referenced is ever missing.
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset, so re-picking the same file still fires change
    if (!file) return;
    const previous = logo?.assetId;
    const assetId = crypto.randomUUID();
    // §17.1.3 The deck's OWN owner key, and never a nugget's: a logo is deck-level (§24.0.1)
    // by definition, so this is one of the two `putAsset` call sites that can name its owner
    // outright rather than resolving one from a slide.
    await putAsset(assetId, deckOwner(deckId), file, file.type);
    dispatch(
      setLogo({
        assetId,
        mime: file.type,
        corner: logo?.corner ?? "br",
        sizePct: logo?.sizePct ?? LOGO_SIZE_DEFAULT,
      }),
    );
    if (previous && previous !== assetId) await deleteAsset(previous);
  };

  const remove = async () => {
    const previous = logo?.assetId;
    dispatch(setLogo(null));
    if (previous) await deleteAsset(previous);
  };

  const setCorner = (corner: LogoCorner) => {
    if (!logo) return;
    dispatch(setLogo({ ...logo, corner }));
  };

  const setSize = (sizePct: number) => {
    if (!logo) return;
    dispatch(setLogo({ ...logo, sizePct }));
  };

  return (
    <div className="flex h-full flex-col gap-5 overflow-y-auto p-4">
      <div>
        <Label className="text-xs uppercase tracking-wide text-slate-500">
          Image
        </Label>
        <div className="mt-2 flex items-center gap-3">
          <div className="flex h-16 w-28 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-50 p-2">
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Deck logo"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <span className="text-xs text-slate-400">None</span>
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
            >
              <ImagePlus className="mr-2 h-4 w-4" />
              {logo ? "Replace" : "Choose image"}
            </Button>
            {logo && (
              <Button variant="outline" size="sm" onClick={remove}>
                <Trash2 className="mr-2 h-4 w-4" /> Remove
              </Button>
            )}
          </div>
        </div>
        {/* SVG passes straight through (§3.1/§24.0.2) — a real logo usually is one. */}
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/webp"
          className="hidden"
          onChange={onFile}
        />
      </div>

      {logo && (
        <>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Corner
            </Label>
            {/* §24.3.2 Drawn as a slide rather than as four words: the choice is spatial,
                and four buttons laid out in the shape of the thing they describe need no
                reading at all. */}
            <ToggleGroup
              type="single"
              value={logo.corner}
              onValueChange={(v) => v && setCorner(v as LogoCorner)}
              variant="outline"
              className="mt-2 grid aspect-[16/9] w-full grid-cols-2 grid-rows-2 gap-1 rounded-md border border-slate-200 bg-slate-50 p-1"
            >
              {LOGO_CORNERS.map((c) => (
                <ToggleGroupItem
                  key={c.value}
                  value={c.value}
                  aria-label={c.label}
                  title={c.label}
                  className={cn(
                    "h-full w-full min-w-0 rounded-sm data-[state=on]:bg-slate-900 data-[state=on]:text-white",
                    // Each cell sits in its own corner of the grid, so the control is a
                    // small picture of the slide.
                    c.value === "tl" && "col-start-1 row-start-1",
                    c.value === "tr" && "col-start-2 row-start-1",
                    c.value === "bl" && "col-start-1 row-start-2",
                    c.value === "br" && "col-start-2 row-start-2",
                  )}
                >
                  <span className="text-[10px] uppercase">{c.value}</span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-slate-500">
                Size
              </Label>
              <span className="text-xs tabular-nums text-slate-500">
                {logo.sizePct}% of slide width
              </span>
            </div>
            <Slider
              value={[logo.sizePct]}
              min={LOGO_SIZE_MIN}
              max={LOGO_SIZE_MAX}
              step={1}
              onValueChange={([v]) => setSize(v)}
            />
          </div>

          <p className="text-xs leading-relaxed text-slate-500">
            The logo is drawn on every slide — the cover included — in the
            editor, in presentation, in the PDF and in the exported HTML.
            Whatever sits in that corner of a slide moves aside to make room.
          </p>
        </>
      )}
    </div>
  );
}
